import React, { FC, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";
import { Variants } from "@kameleon-util/global-theme";
import { v4 as uuidv4 } from "uuid";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { EyecatcherType, Item, layoutOptions } from "./types/prop-types";
import getEvents from "./js/webflow";

/** Eyecatcher Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.None,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.dark,
};

type PassThroughProps = {
  largeHeaderId: string;
};
type EyecatcherWebflowType = WebflowComponentProps<
  EyecatcherType,
  PassThroughProps
>;

const filterEmptyItems = (items: Item[]): Item[] =>
  items.filter((item) => item && item.header.trim() !== "");

const EyecatcherComponent: FC<EyecatcherWebflowType> = ({
  tagline,
  items,
  layoutProps,
  passThrough,
}: EyecatcherWebflowType) => {
  const [largeHeaderId, _] = useState(uuidv4());

  useEffect(() => {
    passThrough?.({ largeHeaderId });
  }, []);

  const filteredItems = filterEmptyItems(items);
  return (
    <Box className="primary-eyecatcher-centered-large-header-media-v1">
      <Box className="padding-global">
        <Box className="container-large">
          <Box className="layout424_component">
            <Box className="layout424_content">
              <Box className="text-align-center">
                {tagline && (
                  <Box className="margin-bottom margin-xsmall tagline">
                    <Variants.TagLine>{tagline}</Variants.TagLine>
                  </Box>
                )}
                <Box className="layout424_heading-list">
                  {filteredItems.map((item, i) => (
                    <Variants.Heading
                      key={`header-${i}`}
                      className={`layout424_heading heading-${i + 1}`}
                    >
                      {item.header}
                    </Variants.Heading>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box data-w-id={largeHeaderId} className="layout424_ix-trigger" />
          </Box>
        </Box>
        {filteredItems
          .slice(0)
          .reverse()
          .map((item, i) => (
            <Box
              key={`image-${i}`}
              className={`layout424_background-image-container background-image-container-${
                filteredItems.length - i
              }`}
            >
              {layoutProps.backgroundColor === "dark" && item.image?.url ? (
                <>
                  <Variants.OpacityLayer className="image-overlay-layer" />
                  <Variants.ImageNoRadius
                    src={item.image.url}
                    loading="lazy"
                    sizes="100vw"
                    alt={item.image.alt}
                    className="layout424_background-image"
                  />
                </>
              ) : (
                <Variants.GridColor
                  index={filteredItems.length - (i + 1)}
                  className="image-overlay-layer"
                />
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC<
  EyecatcherWebflowType,
  PassThroughProps
>(EyecatcherComponent, [
  {
    module: "ix2",
    getEvents: (pageId: any, passThrough): any =>
      getEvents(pageId, passThrough?.largeHeaderId),
  },
]);

export const Eyecatcher = primaryBlock<EyecatcherType>(
  blockConfig,
  ComponentWithWebflow
);
Eyecatcher.defaultProps = getDefaultProps<EyecatcherType>(defaults);

export const Element: KameleonElement = {
  component: Eyecatcher,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
